import { Form, Formik } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, navigate } from 'gatsby';
import React, { useMemo } from 'react';
import { useLocalStorage } from 'react-use';
import * as Yup from 'yup';
import { cn } from '../../utils/cn';

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Provide a valid email address')
    .required('Provide an email address'),
});

const getFormError = ({
  isError,
  error,
  touched,
  errors,
}: {
  isError: boolean;
  error: any;
  touched: any;
  errors: any;
}): string => {
  if (touched.email && errors.email) {
    return errors.email;
  }

  if (isError) {
    return error.message;
  }

  return '';
};

const FormFieldFooter = ({
  isError,
  error,
  touched,
  errors,
}: {
  isError: boolean;
  error: any;
  touched: any;
  errors: any;
}) => {
  const formError = useMemo(
    () => getFormError({ isError, error, touched, errors }),
    [isError, error, touched, errors],
  );
  const hasFormError = !!formError;

  return (
    <>
      {hasFormError ? (
        <p
          key={formError ?? 'form-error'}
          className="block text-sm text-red-500"
        >
          {formError}
        </p>
      ) : (
        <p key="terms" className="block text-sm text-slate-600">
          No set-up or monthly fees.{' '}
          <Link
            to="/terms"
            className="underline transition-colors text-slate-500 hover:text-slate-700 u-ring"
          >
            Terms
          </Link>{' '}
          and{' '}
          <Link
            to="/privacy"
            className="underline transition-colors text-slate-500 hover:text-slate-700 u-ring"
          >
            Privacy Policy
          </Link>
        </p>
      )}
    </>
  );
};

type Props = {
  center?: boolean;
  formId: string;
};

const SignUpForm = ({ center = false, formId }: Props) => {
  const [, setLocalStorageEmail] =
    useLocalStorage<string>('sign-up-form.email');
  const [, setLocalStorageFormId] = useLocalStorage<string>(
    'sign-up-form.form-id',
  );

  const submit = async ({ email }: { email: string }) => {
    setLocalStorageEmail(email);
    setLocalStorageFormId(formId);
    navigate('/get-started/register');
  };

  return (
    <Formik
      initialValues={{ email: '' }}
      onSubmit={submit}
      validationSchema={validationSchema}
    >
      {({
        touched,
        errors,
        values,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
      }) => (
        <Form
          className={cn(
            'flex flex-col items-start flex-grow gap-4 lg:flex-row max-w-full',
            [center ? 'justify-center' : 'justify-start'],
          )}
        >
          <div
            className={cn('w-full lg:inline-block text-left xl:max-w-[360px]', {
              'max-w-[360px]': center,
            })}
          >
            <input
              type="email"
              name="email"
              id="email"
              placeholder="Email address"
              className={cn(
                'bg-white border rounded-lg border-slate-300 text-slate-900 placeholder:text-slate-400 px-3.5 py-3 shadow-sm w-full transition-colors lg:max-w-[360px] max-w-full u-ring mb-1.5',
                {
                  'border-red-500 focus:ring-red-500/20 focus:border-red-500':
                    touched.email && errors.email,
                },
              )}
              value={values.email}
              onChange={(e) => {
                setFieldValue('email', e.target.value);
              }}
              onBlur={() => setFieldTouched('email', true)}
            />
            <div className="h-10 overflow-hidden">
              <FormFieldFooter
                isError={false}
                error={null}
                errors={errors}
                touched={touched}
              />
            </div>
          </div>
          <div
            className={cn('lg:w-auto lg:flex-shrink-0', [
              center ? 'w-full lg:w-auto' : 'w-full lg:flex-grow',
            ])}
          >
            <button
              type="submit"
              className="relative flex-shrink-0 w-full px-5 py-3 font-semibold text-center text-white transition-colors rounded-lg shadow-xs bg-primary u-ring lg:w-auto hover:bg-primary/90 disabled:opacity-50"
              disabled={isSubmitting}
            >
              <AnimatePresence>
                {isSubmitting && (
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                      className="w-5 h-5 animate-spin"
                    >
                      <path
                        fill="white"
                        d="M463.9 376c7.6 4.4 17.5 1.8 21.4-6.1 17.1-34.3 26.7-73 26.7-113.9C512 120 405.9 8.8 272 .5c-8.8-.5-16 6.7-16 15.5s7.2 15.9 16 16.6C388.2 40.8 480 137.7 480 256c0 35.1-8.1 68.3-22.5 97.9-3.9 8-1.3 17.7 6.4 22.2z"
                      ></path>
                    </svg>
                  </motion.div>
                )}
              </AnimatePresence>
              <span className={cn({ 'opacity-0': isSubmitting })}>
                Get started
              </span>
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default SignUpForm;
